import { GraphModel } from '../../graph/graph.model';
import { DatasetModel } from '../model/dataset.model';
import { DG } from '@cotera/sdk/core';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { Assert, assertExists } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { DATASET_ACTION_GROUP } from './constants';
import { AskResponse } from '../../actions/framework/types';
import { useState } from 'react';
import { Text, Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';

type Params = {};

export class DeleteDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Delete Dataset';
  description = 'Deletes the dataset from the graph';
  inputSchema = z.object({});
  group = DATASET_ACTION_GROUP;
  icon = 'trash';

  constructor(
    private readonly navigator: {
      close: (id: string) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
      close: () => void;
    }
  ) {
    super();
  }

  /**
   * There needs to be only 1 dataset in context
   *
   * The dataset needs to have been "saved" at least once.
   */
  applicable(context: ApplicableContext): boolean {
    if (!context.hasTargetedModelOfType(DatasetModel)) {
      return false;
    }

    const dataset = assertExists(context.targetedModelOfType(DatasetModel));

    return !dataset.isNew;
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dataset = context.targetedModelOfType(DatasetModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(dataset !== null, 'Dataset not found');
    Assert.assert(graph !== null, 'Graph not found');

    const datasetName = dataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // by applicable

    // Close the tab BEFORE deleting to avoid rendering the deleted dataset
    this.navigator.close(`/dataset/${datasetName}`);

    const deleteOp = DG.Operations.delete({
      symbol: datasetName,
    });

    const deleteOpResult = await graph.attemptToStageOperations([deleteOp]);

    if (deleteOpResult.isErr()) {
      toast.error(JSON.stringify(deleteOpResult.error.err));
      return err([]);
    }

    dataset.datasetIdentity.set(null);

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const dataset = context.targetedModelOfType(DatasetModel);
    Assert.assert(dataset !== null, 'Dataset not found');
    const datasetName = Assert.assertExists(
      dataset.currentNameInGraph.snapshot()
    );

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();
                }}
                datasetName={datasetName}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  datasetName: string;
}> = ({ onRespond, datasetName }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Delete ${datasetName}`}
      subtitle={'Are you sure you want to delete this dataset?'}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="dataset-name-confirm" className="text-sm">
            Please type the dataset name
          </Label>
          <Input
            id="dataset-name-confirm"
            autoFocus
            placeholder={datasetName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="aria-invalid:border-destructive"
            aria-invalid={error ? true : undefined}
          />
        </div>
        {error && (
          <Text.Caption className="text-destructive">{error}</Text.Caption>
        )}
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            disabled={loading || name !== datasetName}
            variant="destructive"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              if (name === datasetName) {
                setLoading(true);
                await onRespond({});
                setLoading(false);
              } else {
                setError('Incorrect dataset name');
              }
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Delete'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
